/**
 * CBO Import Manager
 * UI for importing CBO data from free public sources and triggering AI enrichment
 * Redesigned with Tailwind + Lucide icons to match IntegrationsDashboard
 */

import React, { useState, useEffect } from 'react';
import {
  Building2,
  CheckCircle,
  Clock,
  Sparkles,
  Map,
  TrendingUp,
  RefreshCw,
  Download,
  FileText,
  Activity,
  AlertCircle,
  Play,
  FastForward,
  Beaker,
  MapPin,
  Zap,
  Phone,
  Mail,
  Globe
} from 'lucide-react';
import { config } from '@/src/config';
import StagingPipelineStatus from './StagingPipelineStatus';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
// Use proxy path in production to avoid CORS issues
// The proxy /provider-api/* maps to https://provider-directory-api.onrender.com/api/*
// So /provider-api/import/cbo/stats -> https://provider-directory-api.onrender.com/api/import/cbo/stats
const getApiBaseUrl = () => {
  // Desktop (Tauri) has no Vercel proxy, so the relative /provider-api path
  // won't resolve — hit the absolute service URL directly, same as dev.
  if (config.isProduction && !config.isDesktop) {
    // Don't add /api here - the proxy already maps to /api
    return '/provider-api';
  }
  // For dev/local/desktop, use the full base URL which includes /api
  return config.providerApi.baseUrl;
};

const API_BASE_URL = getApiBaseUrl();

interface Stats {
  total_providers: number;
  irs_verified: number;
  manually_verified: number;
  unverified: number;
  pending_enrichment: number;
  enriched: number;
  enrichment_failed: number;
  unique_data_sources: number;
  states_covered: number;
  with_ein: number;
  with_ntee_code: number;
  avg_confidence_score: number | null;
}

interface ImportJob {
  id: string;
  job_name: string;
  source_name: string;
  state_code: string | null;
  status: 'completed' | 'running' | 'failed';
  records_created: number;
  records_updated: number;
  records_failed: number;
  created_at: string;
}


interface AsyncJob {
  job_id: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  state: string;
  limit: number | null;
  progress?: {
    processed: number;
    total_estimated: number | string;
    percent: number;
    created: number;
    updated: number;
    failed: number;
    pending_review?: number;
  };
  started_at?: string;
  completed_at?: string;
  elapsed_seconds?: number;
  error?: string;
  results?: any;
  staging_job_id?: number;
}

const CBOImportManager: React.FC = () => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  // State - simplified to just import and jobs. Refinement, auto-approval now in unified Enrichment tab
  const [activeSection, setActiveSection] = useState<'import' | 'jobs'>('import');
  const [stats, setStats] = useState<Stats | null>(null);
  const [importJobs, setImportJobs] = useState<ImportJob[]>([]);
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const [jobDetail, setJobDetail] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(true);

  // Async job tracking
  const [activeAsyncJobs, setActiveAsyncJobs] = useState<AsyncJob[]>([]);
  const [pollingInterval, setPollingInterval] = useState<number | null>(null);

  // Region coverage tracking
  const [regionCoverage, setRegionCoverage] = useState<any>(null);

  // NTEE category selector
  const [nteeCategories, setNteeCategories] = useState<{code: string; label: string; default: boolean}[]>([]);
  const [selectedNtee, setSelectedNtee] = useState<string[]>([]);

  // Preview counts by selected NTEE
  const [previewCounts, setPreviewCounts] = useState<any>(null);

  // IRS regions for bulk import
  const IRS_REGIONS = [
    { key: 'region1', label: 'Northeast', states: 'CT, ME, MA, NH, NJ, NY, RI, VT' },
    { key: 'region2', label: 'Mid-Atlantic & Great Lakes', states: 'DE, DC, IL, IN, IA, KY, MD, MI, MN, NE, NC, ND, OH, PA, SC, SD, VA, WV, WI' },
    { key: 'region3', label: 'Gulf Coast & Pacific Coast', states: 'AL, AK, AR, AZ, CA, CO, FL, GA, HI, ID, KS, LA, MS, MO, MT, NV, NM, OK, OR, TX, TN, UT, WA, WY' },
    { key: 'region4', label: 'Territories & Other', states: 'VI, GU, AS, MP' },
    { key: 'pr', label: 'Puerto Rico', states: 'PR' },
  ];

  // Import form state
  const [importForm, setImportForm] = useState({
    source: 'irs',
    state: 'CA',
    region: '',  // IRS region key for bulk imports
    limit: 10000,
    useBulkDownload: true,
    dataset: 'basic',
    useAsync: true
  });

  // All US states and territories
  const US_STATES = [
    { code: '', name: 'All States' },
    { code: 'AL', name: 'Alabama' },
    { code: 'AK', name: 'Alaska' },
    { code: 'AZ', name: 'Arizona' },
    { code: 'AR', name: 'Arkansas' },
    { code: 'CA', name: 'California' },
    { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' },
    { code: 'DE', name: 'Delaware' },
    { code: 'FL', name: 'Florida' },
    { code: 'GA', name: 'Georgia' },
    { code: 'HI', name: 'Hawaii' },
    { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' },
    { code: 'IN', name: 'Indiana' },
    { code: 'IA', name: 'Iowa' },
    { code: 'KS', name: 'Kansas' },
    { code: 'KY', name: 'Kentucky' },
    { code: 'LA', name: 'Louisiana' },
    { code: 'ME', name: 'Maine' },
    { code: 'MD', name: 'Maryland' },
    { code: 'MA', name: 'Massachusetts' },
    { code: 'MI', name: 'Michigan' },
    { code: 'MN', name: 'Minnesota' },
    { code: 'MS', name: 'Mississippi' },
    { code: 'MO', name: 'Missouri' },
    { code: 'MT', name: 'Montana' },
    { code: 'NE', name: 'Nebraska' },
    { code: 'NV', name: 'Nevada' },
    { code: 'NH', name: 'New Hampshire' },
    { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' },
    { code: 'NY', name: 'New York' },
    { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' },
    { code: 'OH', name: 'Ohio' },
    { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' },
    { code: 'PA', name: 'Pennsylvania' },
    { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' },
    { code: 'SD', name: 'South Dakota' },
    { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' },
    { code: 'UT', name: 'Utah' },
    { code: 'VT', name: 'Vermont' },
    { code: 'VA', name: 'Virginia' },
    { code: 'WA', name: 'Washington' },
    { code: 'WV', name: 'West Virginia' },
    { code: 'WI', name: 'Wisconsin' },
    { code: 'WY', name: 'Wyoming' },
    { code: 'DC', name: 'District of Columbia' },
    { code: 'PR', name: 'Puerto Rico' },
    { code: 'VI', name: 'Virgin Islands' },
    { code: 'GU', name: 'Guam' },
    { code: 'AS', name: 'American Samoa' },
    { code: 'MP', name: 'Northern Mariana Islands' }
  ];


  // Load preview counts for selected NTEE categories
  const loadPreview = async (nteeCodes: string[]) => {
    if (nteeCodes.length === 0) { setPreviewCounts(null); return; }
    try {
      const response = await fetch(`${API_BASE_URL}/import/cbo/import/irs-preview?ntee_codes=${nteeCodes.join(',')}`);
      if (response.ok) {
        setPreviewCounts(await response.json());
      }
    } catch (err) {
      console.warn('Could not load preview:', err);
    }
  };

  // Reload preview when NTEE selection changes
  useEffect(() => {
    if (selectedNtee.length > 0 && importForm.useBulkDownload) {
      const timer = setTimeout(() => loadPreview(selectedNtee), 500); // debounce
      return () => clearTimeout(timer);
    }
  }, [selectedNtee, importForm.useBulkDownload]);

  // Load NTEE categories
  const loadNteeCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/import/cbo/import/ntee-categories`);
      if (response.ok) {
        const data = await response.json();
        setNteeCategories(data.categories);
        setSelectedNtee(data.defaults);
      }
    } catch (err) {
      console.warn('Could not load NTEE categories:', err);
    }
  };

  // Load coverage data
  const loadCoverage = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/import/cbo/import/coverage`);
      if (response.ok) {
        setRegionCoverage(await response.json());
      }
    } catch (err) {
      console.warn('Could not load import coverage:', err);
    }
  };

  // Toggle job detail expansion
  const toggleJobDetail = async (jobId: string) => {
    if (expandedJobId === jobId) {
      setExpandedJobId(null);
      setJobDetail(null);
      return;
    }
    setExpandedJobId(jobId);
    try {
      const response = await fetch(`${API_BASE_URL}/import/cbo/import/jobs/${jobId}`);
      if (response.ok) {
        setJobDetail(await response.json());
      }
    } catch (err) {
      console.warn('Could not load job detail:', err);
    }
  };

  // Load initial data
  useEffect(() => {
    loadStats();
    loadImportJobs();
    loadCoverage();
    loadNteeCategories();
  }, []);

  // No auto-refresh — the periodic stats/jobs refresh caused the page to loop.
  // Metrics live on the Analytics tab; job history on the ETL Pipeline tab.

  const loadStats = async () => {
    try {
      setStatsLoading(true);
      const response = await fetch(`${API_BASE_URL}/import/cbo/stats`);
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const loadImportJobs = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/import/cbo/import/jobs?limit=20`);
      const data = await response.json();
      setImportJobs(data.jobs || []);
    } catch (error) {
      console.error('Failed to load import jobs:', error);
    }
  };

  // Poll for async job status
  const pollAsyncJob = async (jobId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/import/cbo/import/jobs/${jobId}`);

      // If job not found or server error, remove from active jobs
      if (!response.ok) {
        if (response.status === 404 || response.status === 500 || response.status === 502) {
          console.log(`Job ${jobId} not found (likely server restarted), removing from active jobs`);
          setActiveAsyncJobs(prev => prev.filter(j => j.job_id !== jobId));
          return null;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      const jobData: AsyncJob = await response.json();

      // Update the job in our active jobs list
      setActiveAsyncJobs(prev => {
        const existing = prev.find(j => j.job_id === jobId);
        if (!existing) {
          return [...prev, jobData];
        }
        return prev.map(j => j.job_id === jobId ? jobData : j);
      });

      // If job is complete or failed, reload stats
      if (jobData.status === 'completed' || jobData.status === 'failed') {
        await loadStats();
        await loadImportJobs();
      }

      return jobData;
    } catch (error) {
      console.error('Error polling job:', error);
      // Remove job from active list on persistent errors
      setActiveAsyncJobs(prev => prev.filter(j => j.job_id !== jobId));
      return null;
    }
  };

  // Start polling all active jobs
  useEffect(() => {
    const activeJobs = activeAsyncJobs.filter(j => j.status === 'queued' || j.status === 'running');

    if (activeJobs.length > 0) {
      // Poll every 10 seconds
      const interval = setInterval(() => {
        activeJobs.forEach(job => pollAsyncJob(job.job_id));
      }, 10000);

      setPollingInterval(interval);

      return () => clearInterval(interval);
    } else if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  }, [activeAsyncJobs]);

  const handleImport = async () => {
    setLoading(true);
    try {
      let url: string = '';

      if (importForm.source === 'irs') {
        if (importForm.useBulkDownload) {
          const params = new URLSearchParams();
          // Use region for bulk imports (imports all states in the region)
          if (importForm.region) {
            params.append('region', importForm.region);
            // No limit for region imports — import all social service CBOs
          } else if (importForm.state) {
            params.append('state', importForm.state);
            params.append('limit', importForm.limit.toString());
          }
          params.append('dataset', importForm.dataset);
          if (selectedNtee.length > 0) {
            params.append('ntee_codes', selectedNtee.join(','));
          }

          if (importForm.useAsync || importForm.limit > 500) {
            url = `${API_BASE_URL}/import/cbo/import/irs-bulk/async?${params.toString()}`;
          } else {
            url = `${API_BASE_URL}/import/cbo/import/irs-bulk?${params.toString()}`;
          }
        } else {
          url = `${API_BASE_URL}/import/cbo/import/irs?state=${importForm.state}&limit=${importForm.limit}`;
        }
      } else if (importForm.source === 'state') {
        url = `${API_BASE_URL}/import/cbo/import/state/${importForm.state}?limit=${importForm.limit}`;
      }

      const response = await fetch(url, { method: 'POST' });
      const result = await response.json();

      // Check if this is an async job response
      if (result.job_id && result.status === 'queued') {
        const regionLabel = importForm.region
          ? IRS_REGIONS.find(r => r.key === importForm.region)?.label || importForm.region
          : importForm.state || 'ALL';
        alert(`✅ Background import job started!\n\nRegion: ${regionLabel}\nJob ID: ${result.job_id}\n\nThe import is running in the background. You can monitor progress in the Jobs tab.`);

        // Add to active jobs and start polling
        setActiveAsyncJobs(prev => [...prev, {
          job_id: result.job_id,
          status: 'queued',
          state: importForm.state || 'all',
          limit: importForm.limit
        }]);

        // Switch to jobs tab to show progress
        setActiveSection('jobs');
      } else {
        // Synchronous response
        const stateLabel = importForm.state || 'ALL STATES';
        alert(`✅ Import completed!\n\nState: ${stateLabel}\nTotal found: ${result.total}\nNew providers: ${result.new_providers}\nUpdated: ${result.updated_providers}`);
      }

      // Reload data
      await loadStats();
      await loadImportJobs();
    } catch (error: any) {
      console.error('Import failed:', error);
      alert('❌ Import failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickImport = async (state: string, limit: number) => {
    setImportForm({ ...importForm, state, limit });
    setLoading(true);
    try {
      const url = `${API_BASE_URL}/import/cbo/import/irs?state=${state}&limit=${limit}`;
      const response = await fetch(url, { method: 'POST' });
      const result = await response.json();

      alert(`✅ ${state} import completed!\n\nNew providers: ${result.new_providers}`);

      await loadStats();
      await loadImportJobs();
    } catch (error: any) {
      alert('❌ Import failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-3">
                <Building2 className="h-8 w-8 text-purple-600" />
                {t('admin.cbo_import_manager', 'CBO Import Manager')}
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                {t('admin.import_cbos_from_free_public', 'Import CBOs from free public sources and enrich with AI')}
              </p>
            </div>
            <button
              onClick={loadStats}
              className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              <span className="hidden md:inline">{t('admin.refresh', 'Refresh')}</span>
            </button>
          </div>

          {/* Summary metrics moved to the Analytics tab (single metrics surface). */}

          {/* Contact Discovery Info */}
          {stats && (
            <div className="mt-4 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg">
              <div className="flex items-start gap-3">
                <Phone className="h-5 w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-emerald-900 dark:text-emerald-100 mb-1">Contact Discovery Waterfall</h4>
                  <p className="text-xs text-emerald-700 dark:text-emerald-300">
                    IRS-sourced providers are automatically enriched with phone, email, and website via a multi-tier waterfall:
                    web scraping, Hunter.io, Apollo.io, and paid providers. Discovered contacts are synced to both the provider directory and lead builder.
                  </p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-emerald-600 dark:text-emerald-400">
                    <span className="flex items-center gap-1"><Globe className="h-3 w-3" /> Web Scrape</span>
                    <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> Hunter.io</span>
                    <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> Apollo.io</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Geocoding progress / coverage metrics now live on the Analytics tab. */}
        </div>

        {/* Tabs - Refinement and Auto-Approval moved to unified Enrichment tab */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm mb-6">
          <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300">
            <div className="flex w-max min-w-full">
              <Tab
                label={t('admin.import_data', 'Import Data')}
                icon={<Download className="h-4 w-4" />}
                active={activeSection === 'import'}
                onClick={() => setActiveSection('import')}
              />
              <Tab
                label={t('admin.import_history_1', 'Import History')}
                icon={<FileText className="h-4 w-4" />}
                active={activeSection === 'jobs'}
                onClick={() => setActiveSection('jobs')}
              />
            </div>
          </div>
        </div>

        {/* Import Section */}
        {activeSection === 'import' && (
          <div className="w-full">
            {/* Import Form */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <Download className="h-5 w-5 text-purple-600" />
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">{t('admin.import_cbo_data', 'Import CBO Data')}</h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                {t('admin.import_nonprofits_from_free_public', 'Import nonprofits from free public sources')}
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('admin.data_source', 'Data Source')}
                  </label>
                  <select
                    value={importForm.source}
                    onChange={(e) => setImportForm({ ...importForm, source: e.target.value })}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="irs">IRS Nonprofits (Nationwide, FREE)</option>
                    <option value="state">State Portal (CA CHHS)</option>
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('admin.irs_18m_nonprofits_with_financial', 'IRS: 1.8M+ nonprofits with financial data | State: Verified providers')}
                  </p>
                </div>

                {/* Bulk Download Toggle (only show for IRS source) */}
                {importForm.source === 'irs' && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={importForm.useBulkDownload}
                        onChange={(e) => setImportForm({ ...importForm, useBulkDownload: e.target.checked })}
                        className="mt-1 w-4 h-4 text-blue-600 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-blue-900 dark:text-blue-100 flex items-center gap-2">
                          <Download className="h-4 w-4" />
                          {t('admin.use_bulk_download_mode', 'Use Bulk Download Mode')}
                        </div>
                        <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                          Download directly from IRS.gov instead of using ProPublica API.
                          {!importForm.useBulkDownload && ' Use this when API returns errors.'}
                          {importForm.useBulkDownload && ' Bypasses API rate limits and server errors.'}
                        </p>
                      </div>
                    </label>
                  </div>
                )}

                {/* Async Mode Toggle (only show when bulk download is enabled) */}
                {importForm.source === 'irs' && importForm.useBulkDownload && (
                  <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={importForm.useAsync}
                        onChange={(e) => setImportForm({ ...importForm, useAsync: e.target.checked })}
                        className="mt-1 w-4 h-4 text-purple-600 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-purple-900 dark:text-purple-100 flex items-center gap-2">
                          <FastForward className="h-4 w-4" />
                          Background Job Mode (Recommended for &gt;500 records)
                        </div>
                        <p className="text-xs text-purple-700 dark:text-purple-300 mt-1">
                          {importForm.useAsync ?
                            'Import runs in background. No timeout limits - import millions of records!' :
                            'Synchronous mode has 30-second timeout. Use async for large imports.'}
                        </p>
                      </div>
                    </label>
                  </div>
                )}

                {/* Dataset Selector (only show when bulk download is enabled) */}
                {importForm.source === 'irs' && importForm.useBulkDownload && (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                    <label className="block text-sm font-medium text-green-900 dark:text-green-100 mb-2">
                      {t('admin.dataset_type', 'Dataset Type')}
                    </label>
                    <select
                      value={importForm.dataset}
                      onChange={(e) => setImportForm({ ...importForm, dataset: e.target.value })}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-green-300 dark:border-green-600 rounded-lg text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="basic">Basic CSV Data (faster, 1M+ organizations)</option>
                      <option value="form990">Form 990 Filings (detailed, recent filings only)</option>
                    </select>
                    <p className="text-xs text-green-700 dark:text-green-300 mt-2">
                      {importForm.dataset === 'basic'
                        ? '📄 Basic dataset includes EIN, name, address, NTEE code, financials. Covers all tax-exempt orgs.'
                        : '📋 Form 990 dataset includes detailed filing data, mission, programs. Only recent filers (2023).'}
                    </p>
                  </div>
                )}

                {/* NTEE Category Filter (only for bulk downloads) */}
                {importForm.source === 'irs' && importForm.useBulkDownload && nteeCategories.length > 0 && (
                  <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-indigo-900 dark:text-indigo-100">
                        NTEE Categories ({selectedNtee.length} of {nteeCategories.length} selected)
                      </label>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setSelectedNtee(nteeCategories.filter(c => c.default).map(c => c.code))}
                          className="text-[10px] px-2 py-0.5 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-800 rounded"
                        >
                          Reset defaults
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectedNtee(nteeCategories.map(c => c.code))}
                          className="text-[10px] px-2 py-0.5 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-800 rounded"
                        >
                          Select all
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-1 max-h-48 overflow-y-auto">
                      {nteeCategories.map(cat => (
                        <label key={cat.code} className="flex items-center gap-1.5 text-xs cursor-pointer hover:bg-indigo-100/50 dark:hover:bg-indigo-900/30 rounded px-1 py-0.5">
                          <input
                            type="checkbox"
                            checked={selectedNtee.includes(cat.code)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedNtee([...selectedNtee, cat.code]);
                              } else {
                                setSelectedNtee(selectedNtee.filter(c => c !== cat.code));
                              }
                            }}
                            className="w-3 h-3 text-indigo-600 rounded border-gray-300 dark:border-gray-600"
                          />
                          <span className={`font-mono ${cat.default ? 'font-bold text-indigo-800 dark:text-indigo-200' : 'text-gray-700 dark:text-gray-400'}`}>
                            {cat.code}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400 truncate">{cat.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {importForm.useBulkDownload ? (
                  /* Region selector for bulk imports */
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      IRS Region <span className="text-xs text-gray-500 dark:text-gray-400">(imports all states in the region)</span>
                    </label>
                    <select
                      value={importForm.region}
                      onChange={(e) => setImportForm({ ...importForm, region: e.target.value })}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">Select a region...</option>
                      {IRS_REGIONS.map(r => {
                        const preview = previewCounts?.regions?.find((p: any) => p.region_key === r.key);
                        const available = preview?.matching || 0;
                        const coverage = regionCoverage?.regions?.find((rc: any) => rc.region_key === r.key);
                        const imported = coverage?.total_providers_imported || 0;
                        const badge = available > 0
                          ? ` — ${available.toLocaleString()} available${imported > 0 ? `, ${imported.toLocaleString()} imported` : ''}`
                          : imported > 0 ? ` — ${imported.toLocaleString()} imported` : '';
                        return (
                          <option key={r.key} value={r.key}>
                            {r.label}{badge}
                          </option>
                        );
                      })}
                    </select>
                    {importForm.region && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        States: {IRS_REGIONS.find(r => r.key === importForm.region)?.states}
                      </p>
                    )}
                  </div>
                ) : (
                  /* State selector for non-bulk imports */
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        State
                      </label>
                      <select
                        value={importForm.state}
                        onChange={(e) => setImportForm({ ...importForm, state: e.target.value })}
                        className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        {US_STATES.map(state => (
                          <option key={state.code} value={state.code}>
                            {state.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('admin.limit', 'Limit')}
                      </label>
                      <input
                        type="number"
                        value={importForm.limit}
                        onChange={(e) => setImportForm({ ...importForm, limit: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        min="10"
                        max="5000"
                      />
                    </div>
                  </div>
                )}

                {/* Region Coverage Info */}
                {importForm.source === 'irs' && importForm.useBulkDownload && regionCoverage && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Map className="h-4 w-4 text-amber-700 dark:text-amber-300" />
                        <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                          Social Service CBO Coverage
                        </p>
                      </div>
                      <span className="text-xs font-semibold text-amber-800 dark:text-amber-200">
                        {previewCounts
                          ? `${previewCounts.matching_orgs?.toLocaleString()} available for selected NTEE | ${regionCoverage.total_providers_from_irs?.toLocaleString() || 0} imported`
                          : `${regionCoverage.total_providers_from_irs?.toLocaleString() || 0} CBOs imported`
                        }
                      </span>
                    </div>
                    <div className="space-y-2">
                      {regionCoverage.regions
                        .filter((r: any) => r.states.length > 0 && r.region_key !== 'international')
                        .map((region: any) => {
                          const importedCount = Object.keys(region.imported_states).length;
                          const totalStates = region.states.length;
                          return (
                            <div key={region.region_key} className="text-xs">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-amber-800 dark:text-amber-200">{region.label}</span>
                                <div className="flex items-center gap-2">
                                  {(() => {
                                    const preview = previewCounts?.regions?.find((p: any) => p.region_key === region.region_key);
                                    const available = preview?.matching || 0;
                                    const imported = region.total_providers_imported || 0;
                                    return (
                                      <span className="text-amber-700 dark:text-amber-300">
                                        {available > 0 ? `${available.toLocaleString()} avail` : ''}{available > 0 && imported > 0 ? ' / ' : ''}{imported > 0 ? `${imported.toLocaleString()} imported` : available === 0 ? '0' : ''}
                                      </span>
                                    );
                                  })()}
                                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                    importedCount === totalStates
                                      ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
                                      : importedCount === 0
                                        ? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                        : 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200'
                                  }`}>
                                    {importedCount}/{totalStates} states
                                  </span>
                                </div>
                              </div>
                              {region.remaining_states.length > 0 && region.remaining_states.length < totalStates && (
                                <p className="text-amber-600 dark:text-amber-400 mt-0.5">
                                  Remaining: {region.remaining_states.join(', ')}
                                </p>
                              )}
                            </div>
                          );
                        })}
                    </div>
                    <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-2 italic">
                      Only social service NTEE categories (Health, Human Services, Housing, Food, Mental Health, Recreation) are imported from IRS data.
                      Re-importing a region uses cached data and upserts existing records.
                    </p>
                  </div>
                )}

                <button
                  onClick={handleImport}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      {t('admin.importing', 'Importing...')}
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4" />
                      {t('admin.start_import', 'Start Import')}
                    </>
                  )}
                </button>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                    {t('admin.estimated_time', 'Estimated Time:')}
                  </p>
                  <ul className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                    <li>• 100 providers: ~1-2 minutes</li>
                    <li>• 1,000 providers: ~8-15 minutes</li>
                    <li>• 5,000 providers: ~30-45 minutes</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Import History Section */}
        {activeSection === 'jobs' && (
          <div className="space-y-6">
            {/* Active Async Jobs */}
            {activeAsyncJobs.length > 0 && (
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg shadow-sm p-6 border border-purple-200 dark:border-purple-800">
                <div className="flex items-center gap-2 mb-4">
                  <Activity className="h-5 w-5 text-purple-600 animate-pulse" />
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">{t('admin.active_background_jobs', 'Active Background Jobs')}</h3>
                  <span className="ml-auto text-xs text-purple-600 dark:text-purple-400">
                    {t('admin.autorefreshing_every_10_seconds', 'Auto-refreshing every 10 seconds')}
                  </span>
                </div>

                <div className="space-y-4">
                  {activeAsyncJobs.map(job => (
                    <div key={job.job_id} className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-purple-200 dark:border-purple-700">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
                              {job.state} - {job.limit ? `${job.limit.toLocaleString()} records` : 'Unlimited'}
                            </span>
                            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              job.status === 'completed' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' :
                              job.status === 'failed' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200' :
                              job.status === 'running' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 animate-pulse' :
                              'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                            }`}>
                              {job.status === 'queued' && <Clock className="h-3 w-3" />}
                              {job.status === 'running' && <RefreshCw className="h-3 w-3 animate-spin" />}
                              {job.status === 'completed' && <CheckCircle className="h-3 w-3" />}
                              {job.status === 'failed' && <AlertCircle className="h-3 w-3" />}
                              {job.status.toUpperCase()}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Job ID: {job.job_id}
                          </p>
                        </div>
                        {job.elapsed_seconds && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {Math.floor(job.elapsed_seconds / 60)}m {job.elapsed_seconds % 60}s
                          </span>
                        )}
                      </div>

                      {job.progress && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-600 dark:text-gray-400">
                              {job.progress.processed.toLocaleString()} / {typeof job.progress.total_estimated === 'number' ? job.progress.total_estimated.toLocaleString() : job.progress.total_estimated}
                            </span>
                            <span className="font-bold text-purple-600 dark:text-purple-400">
                              {job.progress.percent}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                              className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 rounded-full transition-all duration-500"
                              style={{ width: `${job.progress.percent}%` }}
                            />
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-xs">
                            <div className="text-center">
                              <div className="text-green-600 dark:text-green-400 font-bold">
                                {job.progress.created.toLocaleString()}
                              </div>
                              <div className="text-gray-500 dark:text-gray-400">{t('admin.created', 'Created')}</div>
                            </div>
                            <div className="text-center">
                              <div className="text-blue-600 dark:text-blue-400 font-bold">
                                {job.progress.updated.toLocaleString()}
                              </div>
                              <div className="text-gray-500 dark:text-gray-400">{t('admin.updated', 'Updated')}</div>
                            </div>
                            <div className="text-center">
                              <div className="text-red-600 dark:text-red-400 font-bold">
                                {job.progress.failed.toLocaleString()}
                              </div>
                              <div className="text-gray-500 dark:text-gray-400">{t('admin.failed', 'Failed')}</div>
                            </div>
                          </div>
                        </div>
                      )}

                      {job.error && (
                        <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-xs text-red-700 dark:text-red-300">
                          <strong>{t('admin.error', 'Error:')}</strong> {job.error}
                        </div>
                      )}

                      {/* Staging Pipeline Status */}
                      {job.staging_job_id && (job.status === 'running' || job.status === 'completed') && (
                        <StagingPipelineStatus
                          jobId={job.staging_job_id}
                          apiBaseUrl={`${API_BASE_URL}/import/cbo`}
                          onRefresh={() => {
                            loadStats();
                            pollAsyncJob(job.job_id);
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Import History */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="h-5 w-5 text-gray-600" />
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">{t('admin.import_history', 'Import History')}</h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                {t('admin.recent_import_jobs_and_their', 'Recent import jobs and their status')}
              </p>

            {importJobs.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-16 w-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">{t('admin.no_import_jobs_yet', 'No import jobs yet')}</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  {t('admin.start_your_first_import_from', 'Start your first import from the "Import Data" tab')}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{t('admin.job_name', 'Job Name')}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{t('admin.source', 'Source')}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{t('admin.state', 'State')}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{t('admin.status', 'Status')}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{t('admin.records', 'Records')}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{t('admin.created_1', 'Created')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importJobs.map(job => (
                      <React.Fragment key={job.id}>
                        <tr
                          className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                          onClick={() => toggleJobDetail(job.id)}
                        >
                          <td className="py-3 px-4">
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {expandedJobId === job.id ? '▼' : '▶'} {job.job_name || 'Import Job'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200">
                              {job.source_name || job.source_type}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                            {job.state_code || 'All'}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              job.status === 'completed' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' :
                              job.status === 'running' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200' :
                              'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'
                            }`}>
                              {job.status === 'completed' && <CheckCircle className="h-3 w-3" />}
                              {job.status === 'running' && <Clock className="h-3 w-3" />}
                              {job.status === 'failed' && <AlertCircle className="h-3 w-3" />}
                              {job.status}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex flex-wrap gap-2">
                              <span className="text-xs text-gray-600 dark:text-gray-400">
                                +{(job.records_created || job.imported_records || 0).toLocaleString()}
                              </span>
                              <span className="text-xs text-gray-600 dark:text-gray-400">
                                ~{(job.records_updated || 0).toLocaleString()}
                              </span>
                              {(job.records_failed || job.error_records || 0) > 0 && (
                                <span className="text-xs text-red-600 dark:text-red-400 font-medium">
                                  x{job.records_failed || job.error_records}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-xs text-gray-600 dark:text-gray-400">
                            {job.created_at ? (new Date(job.created_at).getTime() ? fmt.dateTime(job.created_at) : 'N/A') : 'N/A'}
                          </td>
                        </tr>
                        {expandedJobId === job.id && (
                          <tr className="bg-gray-50 dark:bg-gray-800/50">
                            <td colSpan={6} className="px-4 py-3">
                              {jobDetail ? (
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                                  <div>
                                    <p className="text-gray-500 dark:text-gray-400">Total Records</p>
                                    <p className="font-semibold text-gray-900 dark:text-gray-100">
                                      {(jobDetail.total_records || jobDetail.total_records_found || 0).toLocaleString()}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-gray-500 dark:text-gray-400">Imported</p>
                                    <p className="font-semibold text-green-700 dark:text-green-300">
                                      {(jobDetail.imported_records || jobDetail.records_created || 0).toLocaleString()}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-gray-500 dark:text-gray-400">Updated</p>
                                    <p className="font-semibold text-blue-700 dark:text-blue-300">
                                      {(jobDetail.records_updated || 0).toLocaleString()}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-gray-500 dark:text-gray-400">Failed</p>
                                    <p className="font-semibold text-red-700 dark:text-red-300">
                                      {(jobDetail.error_records || jobDetail.records_failed || 0).toLocaleString()}
                                    </p>
                                  </div>
                                  {jobDetail.started_at && (
                                    <div>
                                      <p className="text-gray-500 dark:text-gray-400">Started</p>
                                      <p className="font-medium text-gray-900 dark:text-gray-100">{new Date(jobDetail.started_at).getTime() ? fmt.dateTime(jobDetail.started_at) : 'N/A'}</p>
                                    </div>
                                  )}
                                  {jobDetail.completed_at && (
                                    <div>
                                      <p className="text-gray-500 dark:text-gray-400">Completed</p>
                                      <p className="font-medium text-gray-900 dark:text-gray-100">{new Date(jobDetail.completed_at).getTime() ? fmt.dateTime(jobDetail.completed_at) : 'N/A'}</p>
                                    </div>
                                  )}
                                  {jobDetail.error_log && typeof jobDetail.error_log === 'object' && jobDetail.error_log.error && (
                                    <div className="col-span-2">
                                      <p className="text-gray-500 dark:text-gray-400">Error</p>
                                      <p className="font-medium text-red-700 dark:text-red-300">{jobDetail.error_log.error}</p>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="flex items-center gap-2 text-gray-500">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                                  Loading...
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper Components

interface StatCardProps {
  label: string;
  value: string;
  icon: React.ReactNode;
  color: 'purple' | 'green' | 'orange' | 'blue' | 'teal' | 'indigo';
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon, color }) => {
  const colorClasses = {
    purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
    green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    orange: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    teal: 'bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400',
    indigo: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400',
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 border border-gray-200 dark:border-gray-700">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          {icon}
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
          <p className="text-xs text-gray-600 dark:text-gray-400">{label}</p>
        </div>
      </div>
    </div>
  );
};

interface TabProps {
  label: string;
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
}

const Tab: React.FC<TabProps> = ({ label, active, onClick, icon }) => (
  <button
    onClick={onClick}
    className={`
      px-3 sm:px-6 py-3 font-medium text-xs sm:text-sm whitespace-nowrap flex items-center gap-1 sm:gap-2 flex-shrink-0
      ${active
        ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50 dark:bg-purple-900/20'
        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700'
      }
    `}
  >
    {icon}
    {label}
  </button>
);

interface QuickActionButtonProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled: boolean;
}

const QuickActionButton: React.FC<QuickActionButtonProps> = ({ title, description, icon, onClick, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className="w-full flex items-center gap-4 p-4 bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-lg hover:border-purple-500 dark:hover:border-purple-500 hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-gray-200 dark:disabled:hover:border-gray-600"
  >
    <div className="p-2 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-lg">
      {icon}
    </div>
    <div className="text-left flex-1">
      <div className="font-medium text-gray-900 dark:text-gray-100">{title}</div>
      <div className="text-sm text-gray-600 dark:text-gray-400">{description}</div>
    </div>
  </button>
);

interface FeatureItemProps {
  icon: React.ReactNode;
  label: string;
}

const FeatureItem: React.FC<FeatureItemProps> = ({ icon, label }) => (
  <div className="flex items-center gap-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
    <div className="text-blue-600 dark:text-blue-400">{icon}</div>
    <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
  </div>
);

interface ProgressBarProps {
  label: string;
  value: number;
  total: number;
  color: 'amber' | 'green' | 'red';
  icon: React.ReactNode;
}

const ProgressBar: React.FC<ProgressBarProps> = ({ label, value, total, color, icon }) => {
  const percentage = total > 0 ? (value / total) * 100 : 0;

  const colorClasses = {
    amber: 'bg-amber-500',
    green: 'bg-green-500',
    red: 'bg-red-500',
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          {icon}
          <span>{label}</span>
        </div>
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {value.toLocaleString()}
        </span>
      </div>
      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className={`h-full ${colorClasses[color]} transition-all duration-500`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
    </div>
  );
};

interface StatRowProps {
  label: string;
  value: string;
  icon: React.ReactNode;
}

const StatRow: React.FC<StatRowProps> = ({ label, value, icon }) => (
  <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
      {icon}
      <span>{label}</span>
    </div>
    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{value}</span>
  </div>
);

export default CBOImportManager;
